/* Mobile-only banner shown at the top of complex builder pages
   (PitchEditor, Builder, MgmtEditor, ValuationBuilder).

   - Renders nothing on tablet+ (≥ md breakpoint).
   - Lets the founder copy the deep-link to paste into their desktop browser
     ("Copy link"), or dismiss the banner for the session.
   - Hides itself for 7 days once dismissed (localStorage), so we don't nag.

   Not a "block" — the builder is still fully usable on mobile; this is purely
   a nudge for the best editing experience. */

import { useEffect, useState } from "react";
import { Monitor, Copy, X, CheckCircle } from "@phosphor-icons/react";
import { toast } from "sonner";

const DISMISS_KEY = "clinq:cod-dismissed-at";
const DISMISS_TTL_MS = 7 * 24 * 60 * 60 * 1000; // 7 days

const C = {
  orange: "#F59E0B",
  teal: "#0A8B85",
  tealSoft: "#7BD3CE",
};

export default function ContinueOnDesktopNotice({ surface = "this page" }) {
  const [show, setShow] = useState(false);
  const [copied, setCopied] = useState(false);

  // Decide visibility once on mount + on resize. Simpler than a media-query
  // hook because the banner only needs to flip when the user rotates or
  // resizes (rare in this context).
  useEffect(() => {
    const decide = () => {
      const isMobile = window.matchMedia("(max-width: 767px)").matches;
      if (!isMobile) { setShow(false); return; }
      let dismissedAt = 0;
      try {
        dismissedAt = parseInt(localStorage.getItem(DISMISS_KEY) || "0", 10);
      } catch { dismissedAt = 0; }
      if (Date.now() - dismissedAt < DISMISS_TTL_MS) { setShow(false); return; }
      setShow(true);
    };
    decide();
    window.addEventListener("resize", decide);
    return () => window.removeEventListener("resize", decide);
  }, []);

  if (!show) return null;

  const copyLink = async () => {
    const url = window.location.href;
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      toast.success("Link copied — paste it in your desktop browser to continue.");
      setTimeout(() => setCopied(false), 2500);
    } catch {
      // Fallback for browsers that block clipboard write — show the URL inline
      window.prompt("Copy this link and open it on your desktop:", url);
    }
  };

  const dismiss = () => {
    try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch {}
    setShow(false);
  };

  return (
    <div className="md:hidden px-4 sm:px-6 pt-3" data-testid="continue-on-desktop-notice">
      <div className="rounded-lg p-3 flex items-start gap-2.5"
        style={{
          backgroundColor: "rgba(245, 158, 11, 0.08)",
          border: "1px solid rgba(245, 158, 11, 0.25)",
        }}>
        <Monitor size={16} weight="fill" style={{ color: C.orange, flexShrink: 0, marginTop: 2 }} />
        <div className="flex-1 min-w-0">
          <div className="text-[12px] font-semibold text-white leading-snug">
            Best built on desktop.
          </div>
          <div className="text-[11.5px] text-white/65 leading-snug mt-0.5">
            You can keep editing here, but {surface} has more room to breathe on a larger screen.
          </div>
          <div className="flex items-center gap-2 mt-2.5">
            <button onClick={copyLink} data-testid="cod-copy-link"
              className="inline-flex items-center gap-1.5 text-[11px] font-bold px-3 py-1.5 rounded-md"
              style={{ backgroundColor: C.teal, color: "#fff" }}>
              {copied ? <CheckCircle size={11} weight="bold" /> : <Copy size={11} weight="bold" />}
              {copied ? "Copied!" : "Copy link"}
            </button>
            <button onClick={dismiss} data-testid="cod-dismiss"
              className="text-[11px] font-semibold px-2.5 py-1.5 rounded-md text-white/55 hover:text-white/85">
              Hide for a week
            </button>
          </div>
        </div>
        <button onClick={dismiss} aria-label="Dismiss" data-testid="cod-close"
          className="text-white/35 hover:text-white/75 -mt-0.5 -mr-0.5 p-0.5">
          <X size={14} weight="bold" />
        </button>
      </div>
    </div>
  );
}
